/**
 * RequestToLeaseModal Component
 * Modal for requesting to lease a property
 * Implements S3, S3.1, S3.2, S4, S5 from feedback.md
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../contexts/ToastContext";
import { useCreatePropertyRequestMutation } from "../../store/api/propertyRequestsApi";
import type { Property } from "../../types";
import Icon from "../AppIcon";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select, { type SelectValue } from "../ui/Select";

interface RequestToLeaseModalProps {
	property: Property;
	isOpen: boolean;
	onClose: () => void;
}

const RequestToLeaseModal: React.FC<RequestToLeaseModalProps> = ({
	property,
	isOpen,
	onClose,
}) => {
	const navigate = useNavigate();
	const { success, error: showError } = useToast();
	const [createPropertyRequest, { isLoading }] =
		useCreatePropertyRequestMutation();
	const [currentStep, setCurrentStep] = useState<
		"basic" | "career" | "confirm" | "offer"
	>("basic");

	// Basic Info (S3.1)
	const [basicInfo, setBasicInfo] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
	});

	// Career Info (S3.2)
	const [careerInfo, setCareerInfo] = useState({
		employmentStatus: "" as
			| "employed"
			| "self-employed"
			| "student"
			| "unemployed"
			| "",
		employerName: "",
		businessName: "",
		profession: "",
	});

	// Offer (S5)
	const [offerAmount, setOfferAmount] = useState<number | null>(null);
	const [willingToPayQuoted, setWillingToPayQuoted] = useState(false);

	const handleBasicInfoSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validate phone number format (international format)
		const phoneRegex = /^\+?[1-9]\d{1,14}$/;
		if (!phoneRegex.test(basicInfo.phone.replace(/[\s\-()]/g, ""))) {
			showError(
				"Please enter a valid phone number (e.g., +1234567890 or +2348012345678)",
			);
			return;
		}

		setCurrentStep("career");
	};

	const handleCareerInfoSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		// Validate required fields based on employment status
		if (
			careerInfo.employmentStatus === "employed" ||
			careerInfo.employmentStatus === "self-employed"
		) {
			if (
				careerInfo.employmentStatus === "employed" &&
				!careerInfo.employerName?.trim()
			) {
				showError("Employer name is required for employed status");
				return;
			}
			if (
				careerInfo.employmentStatus === "self-employed" &&
				!careerInfo.businessName?.trim()
			) {
				showError("Business name is required for self-employed status");
				return;
			}
			if (!careerInfo.profession?.trim()) {
				showError("Profession is required");
				return;
			}
		}

		setCurrentStep("confirm");
	};

	const handleConfirmSubmit = async () => {
		if (willingToPayQuoted) {
			// S4: Confirm willingness to pay quoted amount
			await submitRequest();
		} else {
			// S5: Option to make an offer - navigate to offer step
			setCurrentStep("offer");
		}
	};

	const handleOfferSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!offerAmount || offerAmount <= 0) {
			showError("Please enter a valid offer amount");
			return;
		}
		await submitRequest(offerAmount);
	};

	const submitRequest = async (customOffer?: number) => {
		try {
			if (!property.monthly_rent) {
				showError("Property monthly rent is not available");
				return;
			}

			// Convert employment status from frontend format to backend format
			const employmentStatus =
				careerInfo.employmentStatus === "self-employed"
					? "self_employed"
					: careerInfo.employmentStatus;

			// Ensure empty strings are converted to undefined for optional fields
			const employerName =
				careerInfo.employmentStatus === "employed" &&
				careerInfo.employerName?.trim()
					? careerInfo.employerName.trim()
					: undefined;

			const businessName =
				careerInfo.employmentStatus === "self-employed" &&
				careerInfo.businessName?.trim()
					? careerInfo.businessName.trim()
					: undefined;

			const profession = careerInfo.profession?.trim() || undefined;

			await createPropertyRequest({
				property_id: property.id,
				request_type: "lease",
				first_name: basicInfo.firstName,
				last_name: basicInfo.lastName,
				email: basicInfo.email,
				phone: basicInfo.phone,
				employment_status: employmentStatus as
					| "employed"
					| "self_employed"
					| "student"
					| "unemployed",
				employer_name: employerName,
				business_name: businessName,
				profession: profession,
				quoted_amount: Number(property.monthly_rent),
				offer_amount: customOffer ? Number(customOffer) : undefined,
				willing_to_pay_quoted: willingToPayQuoted && !customOffer,
			}).unwrap();

			success(
				"Lease request submitted successfully! Admin will review your request.",
			);
			onClose();
			// Reset form
			setCurrentStep("basic");
			setBasicInfo({ firstName: "", lastName: "", email: "", phone: "" });
			setCareerInfo({
				employmentStatus: "",
				employerName: "",
				businessName: "",
				profession: "",
			});
			setOfferAmount(null);
			setWillingToPayQuoted(false);
		} catch (err: any) {
			console.error("Lease request error:", err);

			// Handle specific error cases
			let errorMessage = "Failed to submit lease request. Please try again.";

			if (err?.status === 429) {
				errorMessage = "Too many requests. Please wait a few minutes before trying again.";
			} else if (err?.status === 400) {
				errorMessage = err?.data?.detail || "Invalid request. Please check your information.";
			} else if (err?.status === 404) {
				errorMessage = "Property not found. It may have been removed.";
			} else if (err?.data?.detail) {
				errorMessage = err.data.detail;
			} else if (err?.data?.message) {
				errorMessage = err.data.message;
			} else if (err?.message) {
				errorMessage = err.message;
			}

			showError(errorMessage);
		}
	};

	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto">
			<div className="bg-card border border-border rounded-lg shadow-xl max-w-2xl w-full my-auto">
				{/* Header */}
				<div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between">
					<h2 className="text-xl font-bold text-foreground">
						Request to Lease: {property.name}
					</h2>
					<button
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground smooth-transition"
					>
						<Icon name="X" size={24} />
					</button>
				</div>

				{/* Content */}
				<div className="p-6">
					{/* Step Indicator */}
					<div className="mb-6 flex items-center justify-center space-x-2">
						{[
							{ label: "Basic Info", step: "basic" },
							{ label: "Career Info", step: "career" },
							{ label: "Confirm", step: "confirm" },
						].map((item, index) => (
							<>
								<div
									key={item.step}
									className={`flex items-center justify-center w-8 h-8 rounded-full ${
										currentStep === item.step
											? "bg-primary text-primary-foreground"
											: currentStep === "offer" ||
											  ["basic", "career", "confirm"].indexOf(currentStep) >
													index
											? "bg-success text-white"
											: "bg-muted text-muted-foreground"
									}`}
								>
									{index + 1}
								</div>
								{index < 2 && (
									<div
										className={`h-1 w-12 ${
											["basic", "career", "confirm"].indexOf(currentStep) >
											index
												? "bg-success"
												: "bg-muted"
										}`}
									/>
								)}
							</>
						))}
					</div>

					{/* Step 1: Basic Info (S3.1) */}
					{currentStep === "basic" && (
						<form onSubmit={handleBasicInfoSubmit} className="space-y-4">
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Basic Information
							</h3>

							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<Input
									label="First Name"
									value={basicInfo.firstName}
									onChange={(e) =>
										setBasicInfo({ ...basicInfo, firstName: e.target.value })
									}
									required
								/>
								<Input
									label="Last Name"
									value={basicInfo.lastName}
									onChange={(e) =>
										setBasicInfo({ ...basicInfo, lastName: e.target.value })
									}
									required
								/>
							</div>

							<Input
								label="Email"
								type="email"
								value={basicInfo.email}
								onChange={(e) =>
									setBasicInfo({ ...basicInfo, email: e.target.value })
								}
								required
							/>

							<Input
								label="Phone Number"
								type="tel"
								value={basicInfo.phone}
								onChange={(e) =>
									setBasicInfo({ ...basicInfo, phone: e.target.value })
								}
								required
							/>

							<div className="flex justify-end space-x-3 pt-4">
								<Button type="button" variant="outline" onClick={onClose}>
									Cancel
								</Button>
								<Button type="submit" disabled={isLoading}>
									Next: Career Info
								</Button>
							</div>
						</form>
					)}

					{/* Step 2: Career Info (S3.2) */}
					{currentStep === "career" && (
						<form onSubmit={handleCareerInfoSubmit} className="space-y-4">
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Career Information
							</h3>

							<Select
								label="Employment Status (S3.2.1)"
								value={careerInfo.employmentStatus}
								onValueChange={(value: SelectValue | SelectValue[]) => {
									const selectedValue = Array.isArray(value) ? value[0] : value;
									setCareerInfo({
										...careerInfo,
										employmentStatus: String(selectedValue) as
											| "employed"
											| "self-employed"
											| "student"
											| "unemployed"
											| "",
									});
								}}
								options={[
									{ value: "", label: "Select employment status" },
									{ value: "employed", label: "Employed" },
									{ value: "self-employed", label: "Self-employed" },
									{ value: "student", label: "Student" },
									{ value: "unemployed", label: "Unemployed" },
								]}
								required
							/>

							{(careerInfo.employmentStatus === "employed" ||
								careerInfo.employmentStatus === "self-employed") && (
								<>
									{careerInfo.employmentStatus === "employed" ? (
										<Input
											label="Employer Name (S3.2.2)"
											value={careerInfo.employerName}
											onChange={(e) =>
												setCareerInfo({
													...careerInfo,
													employerName: e.target.value,
												})
											}
											required
										/>
									) : (
										<Input
											label="Business or Company Name (S3.2.2)"
											value={careerInfo.businessName}
											onChange={(e) =>
												setCareerInfo({
													...careerInfo,
													businessName: e.target.value,
												})
											}
											required
										/>
									)}
									<Input
										label="Profession/Position Held (S3.2.3)"
										value={careerInfo.profession}
										onChange={(e) =>
											setCareerInfo({
												...careerInfo,
												profession: e.target.value,
											})
										}
										required
									/>
								</>
							)}

							<div className="flex justify-end space-x-3 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setCurrentStep("basic")}
								>
									Back
								</Button>
								<Button type="submit">Next: Confirm</Button>
							</div>
						</form>
					)}

					{/* Step 3: Confirm (S4 or S5) */}
					{currentStep === "confirm" && (
						<div className="space-y-4">
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Confirm Lease Request
							</h3>

							<div className="bg-muted/30 rounded-lg p-4 space-y-2">
								<p className="text-sm text-muted-foreground">
									Quoted Monthly Rent:
								</p>
								<p className="text-2xl font-bold text-foreground">
									₦{property.monthly_rent?.toLocaleString()}/month
								</p>
							</div>

							<div className="space-y-4">
								<label className="flex items-start space-x-3 cursor-pointer">
									<input
										type="checkbox"
										checked={willingToPayQuoted}
										onChange={(e) => setWillingToPayQuoted(e.target.checked)}
										className="mt-1"
									/>
									<div>
										<p className="font-medium text-foreground">
											I confirm willingness to pay the quoted lease amount for
											the standard duration (S4)
										</p>
									</div>
								</label>

								{!willingToPayQuoted && (
									<div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
										<p className="text-sm text-warning">
											If you're not pleased with the quoted amount, you can make
											an offer below (S5).
										</p>
									</div>
								)}
							</div>

							<div className="flex justify-end space-x-3 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setCurrentStep("career")}
								>
									Back
								</Button>
								{willingToPayQuoted ? (
									<Button onClick={handleConfirmSubmit} disabled={isLoading}>
										{isLoading ? "Submitting..." : "Submit Request"}
									</Button>
								) : (
									<Button
										type="button"
										onClick={() => setCurrentStep("offer")}
										disabled={isLoading}
									>
										Make an Offer
									</Button>
								)}
							</div>
						</div>
					)}

					{/* Step 4: Make Offer (S5) */}
					{currentStep === "offer" && (
						<form onSubmit={handleOfferSubmit} className="space-y-4">
							<h3 className="text-lg font-semibold text-foreground mb-4">
								Make an Offer (S5)
							</h3>

							<div className="bg-muted/30 rounded-lg p-4 mb-4">
								<p className="text-sm text-muted-foreground mb-2">
									Quoted Monthly Rent:
								</p>
								<p className="text-xl font-bold text-foreground">
									₦{property.monthly_rent?.toLocaleString()}/month
								</p>
							</div>

							<Input
								label="Your Offer Amount (₦)"
								type="number"
								value={offerAmount || ""}
								onChange={(e) =>
									setOfferAmount(parseFloat(e.target.value) || null)
								}
								required
								min={1}
							/>

							<div className="bg-info/10 border border-info/20 rounded-lg p-4">
								<p className="text-sm text-muted-foreground">
									Your offer will be reviewed by the admin. You'll be notified
									via email once a decision is made.
								</p>
							</div>

							<div className="flex justify-end space-x-3 pt-4">
								<Button
									type="button"
									variant="outline"
									onClick={() => setCurrentStep("confirm")}
								>
									Back
								</Button>
								<Button
									type="submit"
									disabled={!offerAmount || offerAmount <= 0 || isLoading}
								>
									{isLoading ? "Submitting..." : "Submit Offer"}
								</Button>
							</div>
						</form>
					)}
				</div>
			</div>
		</div>
	);
};

export default RequestToLeaseModal;
